import React, { useState, useCallback, useEffect, useRef } from 'react';
import { TimelineEditor } from './studio/TimelineEditor';
import { PreviewMonitor } from './studio/PreviewMonitor';
import { InspectorPanel } from './studio/InspectorPanel';
import { ArrowLeftIcon } from './icons/ArrowLeftIcon';
import { UserAssetsModal } from './wizard/UserAssetsModal';
import { v4 as uuidv4 } from 'uuid';
import { Music, Volume2, Type, Stamp, ImagePlus, ChevronDown, Upload, FolderOpen } from 'lucide-react';

export const Studio = ({ projectData, onProjectUpdate, onBack, onGoToDashboard, onAutoSave }) => {
    const [playheadPosition, setPlayheadPosition] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [selectedClipId, setSelectedClipId] = useState(null);
    const [isInspectorCollapsed, setIsInspectorCollapsed] = useState(false);
    const [activeTool, setActiveTool] = useState('select');
    const [openMenu, setOpenMenu] = useState(null); // 'logo' | 'image' | null
    const [assetPickerType, setAssetPickerType] = useState(null); // 'logo' | 'image' | null
    const musicInputRef = useRef(null);
    const sfxInputRef = useRef(null);
    const logoInputRef = useRef(null);
    const imageInputRef = useRef(null);

    // Stage 10: debounced auto-save on Studio timeline edits.
    // Whenever clips, tracks, or assets change, queue a save after a 4s quiet
    // window. Avoids save-per-keystroke while guaranteeing a checkpoint when
    // the user pauses their editing.
    //
    // CRITICAL: hold `onAutoSave` in a ref so the effect's dep array only
    // tracks `timelineSignature`. The autoSave function from App.js is
    // recreated on every render (closure captures live state); putting it in
    // the deps would cause the effect to re-fire every render → schedule a
    // new save → save → re-render → infinite "(followup)(followup)…" loop.
    const onAutoSaveRef = useRef(onAutoSave);
    useEffect(() => { onAutoSaveRef.current = onAutoSave; }, [onAutoSave]);

    const autoSaveDebounceRef = useRef(null);
    const timelineSignature = JSON.stringify({
        clips: (projectData?.timeline?.clips || []).map(c => ({
            id: c.id, type: c.type, startTime: c.startTime, duration: c.duration, content: c.content
        })),
        tracks: (projectData?.timeline?.tracks || []).map(t => ({ id: t.id, type: t.type })),
        assetsCount: (projectData?.assets || []).length,
        overlaysCount: (projectData?.timeline?.overlays || []).length
    });
    useEffect(() => {
        // Skip the initial mount — no user edit has happened yet.
        if (autoSaveDebounceRef.current === null) {
            autoSaveDebounceRef.current = 0;
            return;
        }
        if (autoSaveDebounceRef.current) clearTimeout(autoSaveDebounceRef.current);
        autoSaveDebounceRef.current = setTimeout(() => {
            const fn = onAutoSaveRef.current;
            if (!fn) return;
            try { fn({ reason: 'studio-timeline-edit' }); } catch { /* noop */ }
        }, 4000);
        return () => { if (autoSaveDebounceRef.current) clearTimeout(autoSaveDebounceRef.current); };
    // Intentionally only timelineSignature — see comment above re: ref pattern.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timelineSignature]);

    useEffect(() => {
        if (!projectData.timeline) {
            const newTimeline = {
                tracks: [
                    { id: 'track-video', type: 'video', name: 'Video' },
                    { id: 'track-audio-music', type: 'audio', name: 'Music' },
                    { id: 'track-audio-sfx', type: 'audio', name: 'SFX' },
                    { id: 'track-text', type: 'text', name: 'Text' },
                    { id: 'track-logo', type: 'logo', name: 'Logo' },
                    { id: 'track-image', type: 'image', name: 'Image' }
                ],
                clips: [],
                duration: 10
            };
            onProjectUpdate({ ...projectData, timeline: newTimeline });
        } else {
            // Migrate: ensure logo/image tracks exist on older projects
            let tracks = projectData.timeline.tracks;
            let changed = false;
            if (!tracks.find(t => t.type === 'logo')) { tracks = [...tracks, { id: 'track-logo', type: 'logo', name: 'Logo' }]; changed = true; }
            if (!tracks.find(t => t.type === 'image')) { tracks = [...tracks, { id: 'track-image', type: 'image', name: 'Image' }]; changed = true; }
            if (changed) {
                // Also migrate any logo/image clips sitting on the text track
                const logoTrackId = tracks.find(t => t.type === 'logo')?.id;
                const imageTrackId = tracks.find(t => t.type === 'image')?.id;
                const clips = projectData.timeline.clips.map(c => {
                    if (c.type === 'logo' && logoTrackId && c.trackId !== logoTrackId) return { ...c, trackId: logoTrackId };
                    if (c.type === 'image' && imageTrackId && c.trackId !== imageTrackId) return { ...c, trackId: imageTrackId };
                    return c;
                });
                onProjectUpdate({ ...projectData, timeline: { ...projectData.timeline, tracks, clips } });
            }
        }
    }, []);

    const recalculateDuration = (clips) => {
        const maxEnd = clips.reduce((max, c) => Math.max(max, c.start + c.duration), 0);
        return Math.max(10, maxEnd + 1);
    };

    const handleClipSelected = useCallback((clip) => {
        setSelectedClipId(clip ? clip.id : null);
        if (clip && isInspectorCollapsed) setIsInspectorCollapsed(false);
    }, [isInspectorCollapsed]);

    const handleUpdateClip = useCallback((updatedClip) => {
        if (!projectData.timeline) return;
        
        const newClips = projectData.timeline.clips.map(c => 
            c.id === updatedClip.id ? updatedClip : c
        );
        
        const newDuration = recalculateDuration(newClips);

        onProjectUpdate({
            ...projectData,
            timeline: {
                ...projectData.timeline,
                clips: newClips,
                duration: newDuration
            }
        });
    }, [projectData, onProjectUpdate]);

    const handleDeleteClip = useCallback((clipId) => {
        if (!projectData.timeline) return;
        const newClips = projectData.timeline.clips.filter(c => c.id !== clipId);
        
        const newDuration = recalculateDuration(newClips);

        onProjectUpdate({
            ...projectData,
            timeline: { ...projectData.timeline, clips: newClips, duration: newDuration }
        });
        if (selectedClipId === clipId) setSelectedClipId(null);
    }, [projectData, onProjectUpdate, selectedClipId]);

    const handleAddAsset = useCallback((asset) => {
        onProjectUpdate({
            ...projectData,
            assets: [...projectData.assets, asset]
        });
    }, [projectData, onProjectUpdate]);

    const handleAddTextClip = useCallback(() => {
        if (!projectData.timeline) return;
        const textTrack = projectData.timeline.tracks.find(t => t.type === 'text');
        if (!textTrack) return;

        const newClip = {
            id: `clip-text-${uuidv4()}`,
            type: 'text',
            trackId: textTrack.id,
            start: playheadPosition,
            duration: 5,
            assetId: 'text-asset',
            content: 'New Text',
            fontSize: 12,
            color: '#ffffff',
            fontFamily: 'sans-serif',
            x: 50, y: 50, rotation: 0, opacity: 1
        };

        const newClips = [...projectData.timeline.clips, newClip];
        const newDuration = recalculateDuration(newClips);

        onProjectUpdate({
            ...projectData,
            timeline: { ...projectData.timeline, clips: newClips, duration: newDuration }
        });
        setSelectedClipId(newClip.id);
    }, [projectData, playheadPosition, onProjectUpdate]);

    const handleAddAudioClip = useCallback((asset, trackName) => {
        if (!projectData.timeline) return;
        const audioTrack = projectData.timeline.tracks.find(t => t.name === trackName);
        if (!audioTrack) return;

        const newClip = {
            id: `clip-audio-${uuidv4()}`,
            type: 'audio',
            trackId: audioTrack.id,
            start: playheadPosition,
            duration: 10,
            assetId: asset.id,
            name: asset.name,
            volume: 1
        };

        const newClips = [...projectData.timeline.clips, newClip];
        const newDuration = recalculateDuration(newClips);

        onProjectUpdate({
            ...projectData,
            timeline: { ...projectData.timeline, clips: newClips, duration: newDuration }
        });
        setSelectedClipId(newClip.id);
    }, [projectData, playheadPosition, onProjectUpdate]);

    const handleSplitClip = useCallback((clipId, splitTime) => {
        if (!projectData.timeline) return;
        const clip = projectData.timeline.clips.find(c => c.id === clipId);
        if (!clip) return;

        const relativeSplit = splitTime - clip.start;
        
        if (relativeSplit <= 0.1 || relativeSplit >= clip.duration - 0.1) return;

        const firstPartDuration = relativeSplit;
        const secondPartDuration = clip.duration - relativeSplit;

        const firstClip = { ...clip, duration: firstPartDuration };
        const secondClip = { 
            ...clip, 
            id: `clip-${uuidv4()}`, 
            start: clip.start + firstPartDuration, 
            duration: secondPartDuration 
        };

        const newClips = projectData.timeline.clips
            .filter(c => c.id !== clipId)
            .concat([firstClip, secondClip]);

        const newDuration = recalculateDuration(newClips);

        onProjectUpdate({
            ...projectData,
            timeline: { ...projectData.timeline, clips: newClips, duration: newDuration }
        });
    }, [projectData, onProjectUpdate]);

    const selectedClip = projectData.timeline?.clips.find(c => c.id === selectedClipId) || null;

    const handleAudioUpload = useCallback(async (event, trackName) => {
        const file = event.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            const newAsset = { id: `asset-audio-${uuidv4()}`, type: 'audio', name: file.name, data: reader.result };
            const updatedAssets = [...projectData.assets, newAsset];
            const audioTrack = projectData.timeline?.tracks.find(t => t.name === trackName);
            if (!audioTrack) return;
            const newClip = { id: `clip-audio-${uuidv4()}`, type: 'audio', trackId: audioTrack.id, start: playheadPosition, duration: 10, assetId: newAsset.id, name: file.name, volume: 1 };
            const newClips = [...projectData.timeline.clips, newClip];
            onProjectUpdate({ ...projectData, assets: updatedAssets, timeline: { ...projectData.timeline, clips: newClips, duration: recalculateDuration(newClips) } });
            setSelectedClipId(newClip.id);
        };
        reader.readAsDataURL(file);
        if (event.target) event.target.value = '';
    }, [projectData, playheadPosition, onProjectUpdate]);

    const handleImageUpload = useCallback((event, clipType) => {
        const file = event.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            const newAsset = { id: `asset-${clipType}-${uuidv4()}`, type: clipType, name: file.name, data: reader.result };
            const updatedAssets = [...projectData.assets, newAsset];
            // Find the matching track for this clip type (logo → logo track, image → image track)
            const targetTrack = projectData.timeline?.tracks.find(t => t.type === clipType) 
                || projectData.timeline?.tracks.find(t => t.type === 'text');
            if (!targetTrack) return;
            const newClip = {
                id: `clip-${clipType}-${uuidv4()}`, type: clipType, trackId: targetTrack.id,
                start: playheadPosition, duration: 5, assetId: newAsset.id, name: file.name,
                x: 50, y: 50, width: clipType === 'logo' ? 15 : 40, height: clipType === 'logo' ? 15 : 40,
                rotation: 0, opacity: 1, src: reader.result
            };
            const newClips = [...projectData.timeline.clips, newClip];
            onProjectUpdate({ ...projectData, assets: updatedAssets, timeline: { ...projectData.timeline, clips: newClips, duration: recalculateDuration(newClips) } });
            setSelectedClipId(newClip.id);
            if (isInspectorCollapsed) setIsInspectorCollapsed(false);
        };
        reader.readAsDataURL(file);
        if (event.target) event.target.value = '';
    }, [projectData, playheadPosition, onProjectUpdate, isInspectorCollapsed]);

    // Create a logo/image clip from a library asset URL (no file upload needed)
    const handleImageFromLibrary = useCallback((url, clipType) => {
        if (!url) return;
        const nameFromUrl = (() => {
            try { return decodeURIComponent(url.split('/').pop().split('?')[0]) || `${clipType}-asset`; }
            catch { return `${clipType}-asset`; }
        })();
        const newAsset = { id: `asset-${clipType}-${uuidv4()}`, type: clipType, name: nameFromUrl, data: url };
        const updatedAssets = [...projectData.assets, newAsset];
        const targetTrack = projectData.timeline?.tracks.find(t => t.type === clipType)
            || projectData.timeline?.tracks.find(t => t.type === 'text');
        if (!targetTrack) return;
        const newClip = {
            id: `clip-${clipType}-${uuidv4()}`, type: clipType, trackId: targetTrack.id,
            start: playheadPosition, duration: 5, assetId: newAsset.id, name: nameFromUrl,
            x: 50, y: 50, width: clipType === 'logo' ? 15 : 40, height: clipType === 'logo' ? 15 : 40,
            rotation: 0, opacity: 1, src: url
        };
        const newClips = [...projectData.timeline.clips, newClip];
        onProjectUpdate({ ...projectData, assets: updatedAssets, timeline: { ...projectData.timeline, clips: newClips, duration: recalculateDuration(newClips) } });
        setSelectedClipId(newClip.id);
        if (isInspectorCollapsed) setIsInspectorCollapsed(false);
    }, [projectData, playheadPosition, onProjectUpdate, isInspectorCollapsed]);

    // Close split-menu on outside click / escape
    useEffect(() => {
        if (!openMenu) return;
        const onDown = (e) => {
            if (!e.target.closest?.('[data-menu-anchor]')) setOpenMenu(null);
        };
        const onKey = (e) => { if (e.key === 'Escape') setOpenMenu(null); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [openMenu]);

    if (!projectData.timeline) return <div className="text-white p-8">Initializing Studio...</div>;

    return (
        <div className="flex flex-col h-full text-white overflow-hidden" style={{ background: '#050505' }}>
            {/* Hidden file inputs */}
            <input type="file" accept="audio/*" ref={musicInputRef} className="hidden" onChange={(e) => handleAudioUpload(e, 'Music')} />
            <input type="file" accept="audio/*" ref={sfxInputRef} className="hidden" onChange={(e) => handleAudioUpload(e, 'SFX')} />
            <input type="file" accept="image/*" ref={logoInputRef} className="hidden" onChange={(e) => handleImageUpload(e, 'logo')} />
            <input type="file" accept="image/*" ref={imageInputRef} className="hidden" onChange={(e) => handleImageUpload(e, 'image')} />

            <header className="h-14 border-b border-white/10 flex items-center px-5 shrink-0 justify-between" style={{ background: 'rgba(15, 17, 21, 0.9)' }}>
                <div className="flex items-center gap-4">
                    <button 
                        onClick={onBack}
                        className="flex items-center text-zinc-400 hover:text-white transition-colors text-sm"
                    >
                        <ArrowLeftIcon className="w-4 h-4 mr-1.5" />
                        Back to Storyboard
                    </button>
                    <div className="w-px h-5 bg-white/10" />
                    <h2 className="text-sm font-bold text-white">Studio Editor</h2>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={handleAddTextClip} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-zinc-300 hover:text-white hover:bg-white/10 transition-all text-xs font-medium">
                        <Type className="w-3.5 h-3.5" />
                        Add Text
                    </button>
                    <button onClick={() => musicInputRef.current?.click()} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-zinc-300 hover:text-white hover:bg-white/10 transition-all text-xs font-medium">
                        <Music className="w-3.5 h-3.5" />
                        Music
                    </button>
                    <button onClick={() => sfxInputRef.current?.click()} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-zinc-300 hover:text-white hover:bg-white/10 transition-all text-xs font-medium">
                        <Volume2 className="w-3.5 h-3.5" />
                        SFX
                    </button>
                    <div className="w-px h-5 bg-white/10 mx-0.5" />
                    {/* Logo split-button */}
                    <div className="relative" data-menu-anchor>
                        <button
                            onClick={() => setOpenMenu(openMenu === 'logo' ? null : 'logo')}
                            data-testid="studio-logo-btn"
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-zinc-300 hover:text-white hover:bg-white/10 transition-all text-xs font-medium"
                        >
                            <Stamp className="w-3.5 h-3.5" />
                            Logo
                            <ChevronDown className="w-3 h-3 opacity-70" />
                        </button>
                        {openMenu === 'logo' && (
                            <div
                                data-menu-anchor
                                className="absolute right-0 top-full mt-1 w-48 rounded-lg border border-white/10 bg-zinc-900/95 backdrop-blur-sm shadow-xl z-50 overflow-hidden"
                            >
                                <button
                                    data-testid="studio-logo-upload-option"
                                    onClick={() => { setOpenMenu(null); logoInputRef.current?.click(); }}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-200 hover:bg-white/10 transition-colors text-left"
                                >
                                    <Upload className="w-3.5 h-3.5" />
                                    Upload from device
                                </button>
                                <button
                                    data-testid="studio-logo-library-option"
                                    onClick={() => { setOpenMenu(null); setAssetPickerType('logo'); }}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-200 hover:bg-white/10 transition-colors text-left border-t border-white/5"
                                >
                                    <FolderOpen className="w-3.5 h-3.5" />
                                    Choose from library
                                </button>
                            </div>
                        )}
                    </div>
                    {/* Image split-button */}
                    <div className="relative" data-menu-anchor>
                        <button
                            onClick={() => setOpenMenu(openMenu === 'image' ? null : 'image')}
                            data-testid="studio-image-btn"
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-zinc-300 hover:text-white hover:bg-white/10 transition-all text-xs font-medium"
                        >
                            <ImagePlus className="w-3.5 h-3.5" />
                            Image
                            <ChevronDown className="w-3 h-3 opacity-70" />
                        </button>
                        {openMenu === 'image' && (
                            <div
                                data-menu-anchor
                                className="absolute right-0 top-full mt-1 w-48 rounded-lg border border-white/10 bg-zinc-900/95 backdrop-blur-sm shadow-xl z-50 overflow-hidden"
                            >
                                <button
                                    data-testid="studio-image-upload-option"
                                    onClick={() => { setOpenMenu(null); imageInputRef.current?.click(); }}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-200 hover:bg-white/10 transition-colors text-left"
                                >
                                    <Upload className="w-3.5 h-3.5" />
                                    Upload from device
                                </button>
                                <button
                                    data-testid="studio-image-library-option"
                                    onClick={() => { setOpenMenu(null); setAssetPickerType('image'); }}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-200 hover:bg-white/10 transition-colors text-left border-t border-white/5"
                                >
                                    <FolderOpen className="w-3.5 h-3.5" />
                                    Choose from library
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="w-px h-5 bg-white/10 mx-0.5" />
                    <span className="text-xs text-zinc-500">{projectData.name}</span>
                </div>
            </header>

            <div className="flex-grow flex overflow-hidden">
                <div className="flex-grow flex flex-col min-w-0">
                    <div className="flex-grow flex flex-col min-h-0 relative">
                        <PreviewMonitor 
                            project={projectData} 
                            playheadPosition={playheadPosition}
                            setPlayheadPosition={setPlayheadPosition}
                            isPlaying={isPlaying}
                            setIsPlaying={setIsPlaying}
                            onProjectUpdate={onProjectUpdate}
                            selectedClipId={selectedClipId}
                            onClipSelected={handleClipSelected}
                            onUpdateClip={handleUpdateClip}
                            onGoToDashboard={onGoToDashboard}
                            onAutoSave={onAutoSave}
                        />
                    </div>
                    <div className="h-1/3 border-t border-white/10 min-h-[200px]">
                        <TimelineEditor 
                            project={projectData}
                            playheadPosition={playheadPosition}
                            setPlayheadPosition={setPlayheadPosition}
                            onClipSelected={handleClipSelected}
                            selectedClipId={selectedClipId}
                            onUpdateClipInProgress={handleUpdateClip}
                            onUpdateClipComplete={handleUpdateClip}
                            onDeleteClip={handleDeleteClip}
                            isPlaying={isPlaying}
                            activeTool={activeTool}
                            setActiveTool={setActiveTool}
                            onSplitClip={handleSplitClip}
                        />
                    </div>
                </div>

                <InspectorPanel 
                    selectedClip={selectedClip}
                    project={projectData}
                    onUpdateClip={handleUpdateClip}
                    onDeleteClip={handleDeleteClip}
                    isCollapsed={isInspectorCollapsed}
                    setIsCollapsed={setIsInspectorCollapsed}
                />
            </div>

            {/* User-assets library picker for Logo/Image */}
            {assetPickerType && (
                <UserAssetsModal
                    activeTab={assetPickerType === 'logo' ? 'logos-only' : 'elements'}
                    onSelect={(url) => {
                        handleImageFromLibrary(url, assetPickerType);
                        setAssetPickerType(null);
                    }}
                    onClose={() => setAssetPickerType(null)}
                />
            )}
        </div>
    );
};
